/**
 * Power-ups that are household things (spec 02 §7.5): the alarm clock, the umbrellas, the power
 * heart, the treasure chests and the bomb. On the base at y = 0, facing the camera (+z); colours
 * from the `pu*` ramps.
 */

import { type Color, Vector3 } from 'three';
import { place } from '../../kit/MeshBuilder';
import type { Builder } from './charms';
import { arc, circlePath, grid, lathe, puffy, rotX, rotZ, Smooth, tube } from './shapes';

/** A red twin-bell alarm clock: white dial, black hands at ten o'clock, gold bells and feet. */
export const clock: Builder = (mb, p) => {
  const cy = 0.56;
  const s = new Smooth();
  const casing: Array<[number, number]> = [
    [0, 0], [0.4, 0], [0.46, 0.05], [0.48, 0.15], [0.47, 0.25], [0.43, 0.31], [0, 0.31],
  ];
  lathe(s, casing, 28, () => p.color('puRed.1'));
  s.flush(mb, rotX(0, cy, -0.16, Math.PI / 2));
  tube(s, circlePath(0, cy, 0.4, 28, 0.15), 0.05, 8, p.color('puRed.2'), { closed: true });
  s.flush(mb);
  mb.cylinder(0.37, 0.37, 0.02, 28, p.color('puWhite.2'), rotX(0, cy, 0.14, Math.PI / 2));
  const ink = p.color('ink.0');
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const big = i % 3 === 0;
    const at = rotZ(Math.sin(a) * 0.3, cy + Math.cos(a) * 0.3, -a, 0.16);
    mb.box(big ? 0.05 : 0.03, big ? 0.08 : 0.04, 0.02, ink, at, true);
  }
  mb.box(0.045, 0.27, 0.02, ink, rotZ(0, cy, 0.1, 0.17));
  mb.box(0.055, 0.19, 0.02, ink, rotZ(0, cy, 1.05, 0.175));
  mb.blob(0.04, 0.04, 0.02, p.color('puRed.1'), place(0, cy, 0.19), { detail: 1 });
  // The two bells, the hammer between them, the feet.
  for (const side of [1, -1]) {
    mb.cylinder(0.035, 0.035, 0.14, 6, p.color('puGold.0'), rotZ(side * 0.24, cy + 0.36, -side * 0.6));
    mb.blob(0.19, 0.17, 0.18, p.color('puGold.1'), rotZ(side * 0.33, cy + 0.47, -side * 0.6), {
      topColor: p.color('puGold.2'), flatBottom: true, detail: 3,
    });
    mb.blob(0.08, 0.12, 0.08, p.color('puGold.1'), rotZ(side * 0.3, 0.07, side * 0.5), { detail: 2 });
  }
  mb.cylinder(0.025, 0.025, 0.16, 6, p.color('puGold.0'), place(0, cy + 0.44, 0));
  mb.blob(0.06, 0.05, 0.06, p.color('puGold.1'), place(0, cy + 0.62, 0), { detail: 1 });
};

/** An open umbrella: scalloped canopy panels alternating `ramp` light and mid, a crook handle. */
export const umbrella = (ramp: string): Builder => (mb, p) => {
  const ribs = 8;
  const perPanel = 4;
  const sides = ribs * perPanel;
  const R = 0.72;
  const rows: Vector3[][] = [];
  for (let k = 0; k <= 6; k++) {
    const f = k / 6;
    const ring: Vector3[] = [];
    for (let j = 0; j < sides; j++) {
      const a = (j / sides) * Math.PI * 2;
      const sag = 1 - Math.abs(Math.cos((ribs / 2) * a));
      const r = R * f * (1 - 0.08 * sag * f * f);
      const y = 0.9 + 0.44 * (1 - f * f) + 0.09 * sag * f * f * f;
      ring.push(new Vector3(Math.sin(a) * r, y, Math.cos(a) * r));
    }
    rows.push(ring);
  }
  const s = new Smooth();
  const panel = (_i: number, j: number): Color =>
    p.color(Math.floor(j / perPanel) % 2 === 0 ? `${ramp}.1` : `${ramp}.2`);
  // Rows run from the apex out, so the top is joined rim first (facing out) and the underside,
  // a little below, apex first (facing in): a closed shell.
  grid(s, [...rows].reverse(), panel);
  grid(s, rows.map((ring) => ring.map((v) => v.clone().setY(v.y - 0.03))), () => p.color(`${ramp}.0`));
  s.flush(mb);
  mb.cylinder(0.05, 0, 0.14, 6, p.color('puSteel.1'), place(0, 1.32, 0));
  mb.cylinder(0.03, 0.03, 1.1, 8, p.color('puSteel.2'), place(0, 0.24, 0));
  const hook: Vector3[] = [new Vector3(0, 0.3, 0), new Vector3(0, 0.2, 0)];
  for (let i = 0; i <= 8; i++) hook.push(arc(0.13, 0.19, 0.13, Math.PI + (i / 8) * 1.25 * Math.PI));
  tube(s, hook, 0.055, 10, p.color('puWood.1'));
  s.flush(mb);
  // A tip on the end of every rib.
  for (let i = 0; i < ribs; i++) {
    const a = (i / ribs) * Math.PI * 2;
    const v = rows[6]?.[i * perPanel] ?? new Vector3();
    mb.blob(0.035, 0.035, 0.035, p.color('puSteel.2'), place(Math.sin(a) * (R + 0.02), v.y, Math.cos(a) * (R + 0.02)), {
      detail: 1,
    });
  }
};

/** The power heart: a plump heart of hot pink, a pale shine on its left lobe. */
export const powerHeart: Builder = (mb, p) => {
  const k = 0.042;
  const outline: Array<[number, number]> = [];
  for (let i = 0; i < 64; i++) {
    const t = (i / 64) * Math.PI * 2;
    const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
    outline.push([16 * Math.sin(t) ** 3 * k, y * k]);
  }
  const s = new Smooth();
  puffy(s, outline, [0, -1 * k], 0.3, p.color('puHeart.1'));
  const up = 17 * k + 0.03;
  s.flush(mb, place(0, up, 0));
  mb.blob(0.1, 0.15, 0.05, p.color('puHeart.2'), rotZ(-0.34, up + 0.2, 0.7, 0.2), { detail: 2 });
  mb.blob(0.045, 0.045, 0.04, p.color('puWhite.2'), place(-0.4, up + 0.12, 0.2), { detail: 1 });
};

/** A treasure chest in `ramp`: a barrel lid, straps and lock plate in `trim`. */
export const chest = (ramp: string, trim: string): Builder => (mb, p) => {
  const body = p.color(`${ramp}.1`);
  const t1 = p.color(`${trim}.1`);
  const t2 = p.color(`${trim}.2`);
  mb.box(1.14, 0.5, 0.74, { all: body, top: p.color(`${ramp}.2`), bottom: p.color(`${ramp}.0`) });
  mb.cylinder(0.37, 0.37, 1.12, 20, p.color(`${ramp}.2`), rotZ(-0.56, 0.5, -Math.PI / 2), body);
  for (const x of [-0.36, 0.36]) {
    mb.box(0.12, 0.52, 0.78, t1, place(x, -0.01, 0));
    mb.cylinder(0.39, 0.39, 0.12, 20, t1, rotZ(x - 0.06, 0.5, -Math.PI / 2), t2);
  }
  mb.box(1.18, 0.07, 0.78, t2, place(0, 0.47, 0));
  mb.box(0.24, 0.3, 0.06, { all: t1, front: t2 }, place(0, 0.26, 0.38));
  mb.box(0.05, 0.1, 0.02, p.color('ink.0'), place(0, 0.3, 0.42));
  mb.blob(0.035, 0.035, 0.02, p.color('ink.0'), place(0, 0.42, 0.42), { detail: 1 });
};

/** A round black bomb: a steel cap, a curling fuse, a spark at its end. */
export const bomb: Builder = (mb, p) => {
  mb.blob(0.47, 0.47, 0.47, p.color('puSteel.1'), place(0, 0.48, 0), { detail: 3 });
  mb.cylinder(0.15, 0.15, 0.12, 14, p.color('puWhite.0'), place(0, 0.9, 0), p.color('puWhite.1'));
  const s = new Smooth();
  const fuse = [
    new Vector3(0, 1.0, 0), new Vector3(0.03, 1.08, 0), new Vector3(0.1, 1.14, 0),
    new Vector3(0.2, 1.16, 0), new Vector3(0.28, 1.13, 0),
  ];
  tube(s, fuse, 0.035, 8, p.color('puWood.2'));
  s.flush(mb);
  for (let i = 0; i < 4; i++) {
    mb.blob(0.035, 0.15, 0.035, p.color('bolt.1'), rotZ(0.3, 1.14, (i / 4) * Math.PI), { detail: 1 });
  }
  mb.blob(0.08, 0.08, 0.08, p.color('fire.1'), place(0.3, 1.14, 0), { detail: 2 });
  mb.blob(0.045, 0.045, 0.045, p.color('bolt.2'), place(0.3, 1.14, 0.06), { detail: 1 });
  mb.blob(0.09, 0.14, 0.05, p.color('puSteel.2'), rotZ(-0.2, 0.68, 0.5, 0.38), { detail: 2 });
  mb.blob(0.035, 0.035, 0.03, p.color('puWhite.2'), place(-0.25, 0.78, 0.36), { detail: 1 });
};
